import * as stylex from '@stylexjs/stylex';
import {
  CatchBoundary,
  createFileRoute,
  Outlet,
  useMatches,
  useRouter,
} from '@tanstack/react-router';
import { type ReactElement } from 'react';
import { Provider } from 'react-aria-components';

import { ErrorState } from '@/components/error-state.component';
import { NotFoundState } from '@/components/not-found-state.component';
import { PendingState } from '@/components/pending-state.component';
import {
  DETAILS_TOOLBAR_SLOTS,
  DetailsToolbar,
  Heading,
  TableViewContext,
  Text,
  View,
  ViewContext,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { $ } from '@/libs/serenity-ui/tokens.stylex';

export const Route = createFileRoute('/_main/_details')({
  component: RouteComponent,
  pendingComponent: PendingState,
  notFoundComponent: NotFoundState,
});

function RouteComponent(): ReactElement {
  const router = useRouter();
  const matches = useMatches();

  const childMatch = matches.at(-1);
  const staticData = childMatch?.staticData?.details;

  const isHighlighted = router.history.location.hash === '#details';

  return (
    <View style={[ styles.base, isHighlighted && styles.highlighted ]}>
      <DetailsToolbar>
        <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
          {staticData?.slots?.topBarComponent &&
        <View slot={DETAILS_TOOLBAR_SLOTS.COLLAPSIBLE_ROW_VIEW}>
          <staticData.slots.topBarComponent />
        </View>}
        </CatchBoundary>

        <View slot={DETAILS_TOOLBAR_SLOTS.ROW_VIEW}>
          <View slot={DETAILS_TOOLBAR_SLOTS.PAGE_HEADER_VIEW}>
            <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
              <Heading>{typeof staticData?.heading === 'function' ? <staticData.heading /> : staticData?.heading}</Heading>
            </CatchBoundary>

            <View slot={DETAILS_TOOLBAR_SLOTS.PAGE_SUBTITLE_VIEW}>
              <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
                {staticData?.slots?.idComponent && <staticData.slots.idComponent />}
                {staticData?.pageType && <Text slot={DETAILS_TOOLBAR_SLOTS.PAGE_TYPE_TEXT}>{staticData.pageType}</Text>}
              </CatchBoundary>
            </View>
            <View style={actionsView.base}>
              <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
                {staticData?.slots?.statusComponent && <staticData.slots.statusComponent />}
                {staticData?.slots?.actionsComponent && <staticData.slots.actionsComponent />}
              </CatchBoundary>
            </View>
          </View>
        </View>
      </DetailsToolbar>

      <View style={contentStyles.base}>
        <Provider values={[[ ViewContext, { style: contentStyles.view }], [ TableViewContext, { style: tableViewStyles.base }]]}>
          <CatchBoundary
            errorComponent={ErrorState}
            getResetKey={() => 'reset'}>
            <Outlet />
          </CatchBoundary>
        </Provider>
      </View>
    </View>
  );
}

const styles = stylex.create({
  base: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  highlighted: {
    borderColor: $.accentChilli60,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
    boxShadow: `0 0 8px 4px ${$.colorChilli10}`,
  },
});

const actionsView = stylex.create({
  base: {
    marginLeft: 'auto',
  },
});

const tableViewStyles = stylex.create({
  base: {
    width: {
      default: 'fit-content',
      [breakpoints.small]: '100%',
    },
  },
});

const contentStyles = stylex.create({
  base: {
    alignItems: 'flex-start',
    alignSelf: 'center',
    display: 'grid',
    gap: 40,
    maxWidth: '100%',
    paddingBottom: 120,
    paddingInline: 8,
    paddingTop: 24,
    width: {
      default: 'min(100%, 1520px)',
      [breakpoints.small]: '100%',
    },
  },
  view: {
    display: 'grid',
    gap: 40,
    gridColumn: '1 / -1',
    gridTemplateColumns: {
      default: 'repeat(auto-fit, minmax(360px, min-content))',
      [breakpoints.small]: '1fr',
    },
    maxWidth: '100%',
    width: '100%',
  },
});
